import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useRef, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  PencilIcon,
  ArrowLeftIcon,
  TrashIcon,
  DocumentTextIcon,
  WrenchScrewdriverIcon,
  PlusIcon,
  ArrowDownTrayIcon,
  CloudArrowUpIcon,
  ClockIcon,
  QrCodeIcon,
  ShoppingCartIcon,
  DocumentArrowDownIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/20/solid'
import { CubeIcon, CubeTransparentIcon, LinkIcon } from '@heroicons/react/24/outline'
import { assetsApi, serviceCasesApi, twinApi, type TwinProject } from '../../lib/api'
import { useTranslation } from '../../hooks/useTranslation'
import { useCartridgeEntityTabs } from '../../hooks/useCartridgeUI'
import type { TranslationKey } from '../../../locales/types'
import { AssetForm } from '../../components/forms/AssetForm'
import { AssetStatusBadge } from '../../components/ui/AssetStatusBadge'
import { ServiceCaseStatusBadge } from '../../components/ui/ServiceCaseStatusBadge'
import { ServiceCasePriorityBadge } from '../../components/ui/ServiceCasePriorityBadge'
import { ServiceCaseCategoryBadge } from '../../components/ui/ServiceCaseCategoryBadge'
import { TwinStatusBadge } from '../../components/TwinViewer'
import { TwinBomViewer } from '../../components/TwinBomViewer'
import { CADUpload } from '../../components/CADUpload'
import { CADViewer } from '../../components/CADViewer'
import {
  CartridgeTabContent,
  useCartridgeTabInfo,
  type CartridgeTabInfo,
} from '../../components/cartridge/CartridgeEntityTabs'
import type { AssetInput } from '../../../shared/types'

// Static tab type (for built-in tabs)
type StaticTabType = 'details' | 'twin' | 'documents' | 'bom' | 'cases' | 'history'
// Combined tab type includes cartridge tabs (prefixed with 'cartridge:')
type TabType = StaticTabType | string

// Document type icons/colors mapping
const DOCUMENT_TYPE_CONFIG: Record<string, { color: string; key: string }> = {
  manual: { color: 'bg-blue-100 text-blue-700', key: 'manual' },
  wiring_diagram: { color: 'bg-orange-100 text-orange-700', key: 'wiring_diagram' },
  ce_declaration: { color: 'bg-green-100 text-green-700', key: 'ce_declaration' },
  parts_list: { color: 'bg-purple-100 text-purple-700', key: 'parts_list' },
  maintenance_schedule: { color: 'bg-cyan-100 text-cyan-700', key: 'maintenance_schedule' },
  other: { color: 'bg-gray-100 text-gray-700', key: 'other' },
}

export function AssetDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const [isEditing, setIsEditing] = useState(false)
  const [activeTab, setActiveTab] = useState<TabType>('details')
  const [showQrCode, setShowQrCode] = useState(false)
  const [showUploadForm, setShowUploadForm] = useState(false)
  const [downloadingDoc, setDownloadingDoc] = useState<string | null>(null)
  const [showLinkTwinModal, setShowLinkTwinModal] = useState(false)
  const [selectedTwinProject, setSelectedTwinProject] = useState<string>('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Upload form state
  const [uploadTitle, setUploadTitle] = useState('')
  const [uploadType, setUploadType] = useState('manual')
  const [uploadLanguage, setUploadLanguage] = useState('en')
  const [uploadFile, setUploadFile] = useState<File | null>(null)

  const { data: asset, isLoading } = useQuery({
    queryKey: ['assets', id],
    queryFn: () => assetsApi.get(id!),
    enabled: !!id,
  })

  // Fetch related service cases
  const { data: casesResponse } = useQuery({
    queryKey: ['service-cases', { assetId: id }],
    queryFn: () => serviceCasesApi.list({ filters: { assetId: id! } }),
    enabled: !!id && activeTab === 'cases',
  })

  // Fetch documents
  const { data: documents, refetch: refetchDocuments } = useQuery({
    queryKey: ['assets', id, 'documents'],
    queryFn: () => assetsApi.getDocuments(id!),
    enabled: !!id && activeTab === 'documents',
  })

  // Fetch external document from Twin Engine (if asset has twinSampleId)
  const { data: externalDocument } = useQuery({
    queryKey: ['twin-sample-document', asset?.twinSampleId],
    queryFn: () => twinApi.getSample(asset!.twinSampleId!),
    enabled: !!asset?.twinSampleId && activeTab === 'documents',
  })

  // Fetch BOM items (needed for both 'bom' and 'twin' tabs for combined view)
  const { data: bomItems } = useQuery({
    queryKey: ['assets', id, 'bom'],
    queryFn: () => assetsApi.getBom(id!),
    enabled: !!id && (activeTab === 'bom' || activeTab === 'twin'),
  })

  // Fetch history
  const { data: history } = useQuery({
    queryKey: ['assets', id, 'history'],
    queryFn: () => assetsApi.getHistory(id!),
    enabled: !!id && activeTab === 'history',
  })

  // Fetch available Twin projects for linking (only unlinked projects)
  const { data: availableTwinProjects } = useQuery({
    queryKey: ['twin-projects', 'available'],
    queryFn: async () => {
      const allProjects = await twinApi.listProjects()
      // Get all assets to find which have twin projects linked
      const assetsResponse = await assetsApi.list({ limit: 1000 })
      const linkedTwinIds = new Set(
        assetsResponse.data
          ?.filter(a => a.twinProjectId && a.id !== id)
          .map(a => a.twinProjectId) || []
      )
      // Return only projects that are not linked to other assets
      return allProjects.filter(p => !linkedTwinIds.has(p.id))
    },
    enabled: showLinkTwinModal && !asset?.twinProjectId,
  })

  const updateMutation = useMutation({
    mutationFn: (data: Partial<AssetInput>) => assetsApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets', id] })
      queryClient.invalidateQueries({ queryKey: ['assets'] })
      setIsEditing(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => assetsApi.delete(id!),
    onSuccess: () => {
      navigate('/assets')
    },
  })

  // Link Twin project to asset
  const linkTwinMutation = useMutation({
    mutationFn: (twinProjectId: string) => assetsApi.update(id!, { twinProjectId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets', id] })
      queryClient.invalidateQueries({ queryKey: ['twin-projects'] })
      setShowLinkTwinModal(false)
      setSelectedTwinProject('')
    },
  })

  // Unlink Twin project from asset
  const unlinkTwinMutation = useMutation({
    mutationFn: () => assetsApi.update(id!, { twinProjectId: null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets', id] })
      queryClient.invalidateQueries({ queryKey: ['twin-projects'] })
    },
  })

  // Handle CAD upload success - link the new project to this asset
  const handleTwinProjectCreated = (project: TwinProject) => {
    linkTwinMutation.mutate(project.id)
  }

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!uploadFile) throw new Error('No file selected')
      return assetsApi.uploadDocument(id!, uploadFile, {
        title: uploadTitle,
        documentType: uploadType,
        language: uploadLanguage,
      })
    },
    onSuccess: () => {
      refetchDocuments()
      setShowUploadForm(false)
      setUploadTitle('')
      setUploadType('manual')
      setUploadLanguage('en')
      setUploadFile(null)
    },
  })

  const handleDownload = async (docId: string) => {
    setDownloadingDoc(docId)
    try {
      const { url } = await assetsApi.getDocumentDownloadUrl(id!, docId)
      // Open in new tab for download
      window.open(url, '_blank')
    } catch (error) {
      console.error('Download failed:', error)
    } finally {
      setDownloadingDoc(null)
    }
  }

  // Get cartridge entity tabs for 'asset' type
  const { tabs: cartridgeTabs } = useCartridgeEntityTabs('asset')
  const cartridgeTabInfos = useCartridgeTabInfo(cartridgeTabs)

  // Build static tabs
  const staticTabs = useMemo(() => [
    { key: 'details' as const, label: t('common.details'), icon: CubeIcon, position: 0 },
    { key: 'twin' as const, label: t('twin.asset.tabTitle'), icon: CubeTransparentIcon, position: 10 },
    { key: 'documents' as const, label: t('common.documents'), icon: DocumentTextIcon, position: 20 },
    { key: 'bom' as const, label: t('pages.assets.bom'), icon: WrenchScrewdriverIcon, position: 30 },
    { key: 'cases' as const, label: t('nav.serviceCases'), icon: WrenchScrewdriverIcon, position: 40 },
    { key: 'history' as const, label: t('audit.history'), icon: ClockIcon, position: 50 },
  ], [t])

  // Merge static tabs with cartridge tabs
  const allTabs = useMemo(() => {
    const merged = [
      ...staticTabs,
      ...cartridgeTabInfos.map(info => ({
        key: info.key,
        label: info.label,
        icon: info.icon,
        position: info.tab.tab.position ?? 100,
        isCartridge: true as const,
        cartridgeData: info.tab,
      })),
    ]
    return merged.sort((a, b) => (a.position ?? 50) - (b.position ?? 50))
  }, [staticTabs, cartridgeTabInfos])

  // Helper to check if current tab is a cartridge tab
  const isCartridgeTab = (key: string) => key.startsWith('cartridge:')
  const getCartridgeTabData = (key: string) => {
    return cartridgeTabs.find(t => `cartridge:${t.cartridgeSlug}:${t.tab.key}` === key)
  }

  if (isLoading) return <div className="p-8">{t('common.loading')}</div>
  if (!asset) return <div className="p-8">{t('pages.assets.notFound')}</div>

  const cases = casesResponse?.data || []

  // For backward compatibility - static tabs reference
  const tabs = staticTabs

  // Generate QR code URL (using a simple QR code service or placeholder)
  const qrCodeUrl = asset.qrCodeUrl || `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
    `${window.location.origin}/assets/${asset.id}`
  )}`

  return (
    <>
      {/* Secondary column (desktop sidebar) */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="mb-6">
          <Link
            to="/assets"
            className="inline-flex items-center gap-x-2 text-sm font-semibold hover:opacity-75 text-brand-500"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            {t('pages.assets.backToList')}
          </Link>
        </div>

        <div className="mb-6">
          <div className="flex items-center gap-x-2 mb-2">
            <CubeIcon className="h-5 w-5 text-gray-400" />
            <span className="text-lg font-bold text-gray-900">{asset.serialNumber}</span>
          </div>
          <div className="mt-2">
            <AssetStatusBadge status={asset.status} />
          </div>
        </div>

        <div className="space-y-3 mb-6">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="w-full inline-flex justify-center items-center gap-x-2 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
          >
            <PencilIcon className="h-4 w-4" />
            {isEditing ? t('common.cancel') : t('common.edit')}
          </button>
          <Link
            to={`/service-cases/new?assetId=${asset.id}`}
            className="w-full inline-flex justify-center items-center gap-x-2 rounded-md bg-brand-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-500"
          >
            <PlusIcon className="h-4 w-4" />
            {t('pages.assets.createServiceCase')}
          </Link>
          <button
            onClick={() => setShowQrCode(!showQrCode)}
            className="w-full inline-flex justify-center items-center gap-x-2 rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200"
          >
            <QrCodeIcon className="h-4 w-4" />
            {showQrCode ? t('pages.assets.hideQrCode') : t('pages.assets.showQrCode')}
          </button>
          <button
            onClick={() => {
              if (confirm(t('pages.assets.confirmDelete'))) {
                deleteMutation.mutate()
              }
            }}
            className="w-full inline-flex justify-center items-center gap-x-2 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
          >
            <TrashIcon className="h-4 w-4" />
            {t('common.delete')}
          </button>
        </div>

        {/* QR Code Display */}
        {showQrCode && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 text-center">{t('pages.assets.qrCodeTitle')}</h3>
            <div className="flex justify-center">
              <img
                src={qrCodeUrl}
                alt={`QR Code for ${asset.serialNumber}`}
                className="w-40 h-40 border border-gray-200 rounded"
              />
            </div>
            <p className="text-xs text-gray-500 text-center mt-2">
              {t('pages.assets.qrCodeDescription')}
            </p>
          </div>
        )}

        <div className="border-t border-gray-200 pt-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">{t('common.quickInfo')}</h2>
          <dl className="space-y-4">
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.account')}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {asset.account ? (
                  <Link to={`/accounts/${asset.accountId}`} className="text-brand-600 hover:text-brand-800">
                    {asset.account.name}
                  </Link>
                ) : (
                  '-'
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('pages.assets.productLine')}</dt>
              <dd className="mt-1 text-sm text-gray-900">{asset.productLine || '-'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('pages.assets.model')}</dt>
              <dd className="mt-1 text-sm text-gray-900">{asset.model || '-'}</dd>
            </div>
            {asset.commissioningDate && (
              <div>
                <dt className="text-xs font-medium text-gray-500">{t('pages.assets.commissioned')}</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(asset.commissioningDate).toLocaleDateString()}
                </dd>
              </div>
            )}
            {asset.warrantyExpiry && (
              <div>
                <dt className="text-xs font-medium text-gray-500">{t('pages.assets.warrantyStatus')}</dt>
                <dd className="mt-1">
                  {new Date(asset.warrantyExpiry) > new Date() ? (
                    <span className="inline-flex items-center gap-1 text-sm text-green-600">
                      <CheckCircleIcon className="h-4 w-4" />
                      {t('pages.assets.activeUntil')} {new Date(asset.warrantyExpiry).toLocaleDateString()}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-sm text-red-600">
                      <XCircleIcon className="h-4 w-4" />
                      {t('pages.assets.expired')} {new Date(asset.warrantyExpiry).toLocaleDateString()}
                    </span>
                  )}
                </dd>
              </div>
            )}
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.created')}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {new Date(asset.createdAt).toLocaleDateString()}
              </dd>
            </div>
          </dl>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] p-6">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <div className="mb-4">
              <Link
                to="/assets"
                className="inline-flex items-center gap-x-2 text-sm font-semibold hover:opacity-75 text-brand-500"
              >
                <ArrowLeftIcon className="h-5 w-5" />
                {t('pages.assets.backToList')}
              </Link>
            </div>
            <div className="flex items-center gap-x-2 mb-2">
              <CubeIcon className="h-5 w-5 text-gray-400" />
              <span className="font-bold text-gray-900">{asset.serialNumber}</span>
              <AssetStatusBadge status={asset.status} />
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8 overflow-x-auto">
              {allTabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === tab.key
                      ? 'border-brand-500 text-brand-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {isEditing ? (
            <div>
              <h2 className="text-lg font-medium mb-4">{t('pages.assets.editTitle')}</h2>
              <AssetForm
                defaultValues={{
                  serialNumber: asset.serialNumber,
                  accountId: asset.accountId,
                  contactId: asset.contactId,
                  productLine: asset.productLine,
                  model: asset.model,
                  status: asset.status,
                  commissioningDate: asset.commissioningDate ? new Date(asset.commissioningDate).toISOString().slice(0, 16) : undefined,
                  warrantyExpiry: asset.warrantyExpiry ? new Date(asset.warrantyExpiry).toISOString().slice(0, 16) : undefined,
                  notes: asset.notes,
                }}
                onSubmit={(data) => updateMutation.mutate(data)}
                onCancel={() => setIsEditing(false)}
                loading={updateMutation.isPending}
              />
            </div>
          ) : (
            <>
              {/* Details Tab */}
              {activeTab === 'details' && (
                <div className="space-y-6">
                  {/* Configuration */}
                  {asset.configuration && Object.keys(asset.configuration).length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('pages.assets.configuration')}</h3>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                          {Object.entries(asset.configuration as Record<string, unknown>).map(([key, value]) => (
                            <div key={key} className="flex flex-col">
                              <dt className="text-xs font-medium text-gray-500 capitalize">
                                {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
                              </dt>
                              <dd className="mt-0.5 text-sm text-gray-900">
                                {typeof value === 'object' && value !== null
                                  ? Object.entries(value as Record<string, unknown>).map(([k, v]) => (
                                      <span key={k} className="inline-flex items-center mr-2 mb-1 px-2 py-0.5 rounded-full text-xs bg-gray-200 text-gray-700">
                                        {k}: {String(v)}
                                      </span>
                                    ))
                                  : typeof value === 'boolean'
                                    ? (value ? '✓ Yes' : '✗ No')
                                    : String(value) || '-'}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {asset.notes && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('pages.assets.notes')}</h3>
                      <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-4">
                        {asset.notes}
                      </div>
                    </div>
                  )}

                  {!asset.configuration && !asset.notes && (
                    <p className="text-sm text-gray-500 text-center py-8">
                      {t('pages.assets.noDetails')}
                    </p>
                  )}
                </div>
              )}

              {/* Twin / 3D Model Tab */}
              {activeTab === 'twin' && (
                <div className="space-y-4">
                  {(asset.twinProjectId || asset.twinSampleId) ? (
                    // Has Twin project or sample linked - show viewer
                    <>
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-900">{t('twin.title')}</h3>
                        {asset.twinProjectId && (
                          <div className="flex items-center gap-3">
                            <Link
                              to={`/twin/${asset.twinProjectId}`}
                              className="text-xs text-brand-600 hover:text-brand-700"
                            >
                              {t('common.viewDetails')}
                            </Link>
                            <button
                              onClick={() => {
                                if (confirm(t('twin.detail.confirmDelete'))) {
                                  unlinkTwinMutation.mutate()
                                }
                              }}
                              className="text-xs text-red-600 hover:text-red-700"
                            >
                              {t('twin.detail.unlinkAsset')}
                            </button>
                          </div>
                        )}
                      </div>
                      {/* Use combined viewer if BOM items exist, otherwise basic viewer */}
                      {bomItems && bomItems.length > 0 ? (
                        <TwinBomViewer
                          assetId={asset.id}
                          projectId={asset.twinProjectId}
                          sampleId={asset.twinSampleId}
                          bomItems={bomItems}
                          className="h-[600px] rounded-lg border border-gray-200 overflow-hidden"
                          onBomItemUpdate={(updatedItem) => {
                            // Refresh BOM items when a link is updated
                            queryClient.invalidateQueries({ queryKey: ['assets', asset.id, 'bom'] })
                          }}
                        />
                      ) : (
                        <div className="flex justify-center">
                          <CADViewer
                            projectId={asset.twinProjectId ?? undefined}
                            sampleId={asset.twinSampleId ?? undefined}
                            className="w-3/4 aspect-square"
                            onPartSelect={(partName) => {
                              console.log('Part selected:', partName)
                            }}
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    // No Twin project - show upload or link options
                    <div className="space-y-6">
                      <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                        <CubeTransparentIcon className="mx-auto h-12 w-12 text-gray-300" />
                        <h3 className="mt-3 text-sm font-semibold text-gray-900">{t('twin.asset.noTwin')}</h3>
                        <p className="mt-1 text-sm text-gray-500">
                          {t('twin.viewer.uploadPrompt')}
                        </p>
                      </div>

                      {/* Upload new CAD file */}
                      <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <h4 className="text-sm font-medium text-gray-900 mb-4 flex items-center gap-2">
                          <CloudArrowUpIcon className="h-5 w-5 text-brand-500" />
                          {t('twin.asset.uploadNew')}
                        </h4>
                        <CADUpload
                          assetName={asset.serialNumber}
                          onProjectCreated={handleTwinProjectCreated}
                          onError={(error) => console.error('CAD upload error:', error)}
                        />
                      </div>

                      {/* Divider */}
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-gray-200" />
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className="bg-white px-3 text-gray-500">{t('twin.asset.orDivider')}</span>
                        </div>
                      </div>

                      {/* Link existing project */}
                      <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <h4 className="text-sm font-medium text-gray-900 mb-4 flex items-center gap-2">
                          <LinkIcon className="h-5 w-5 text-brand-500" />
                          {t('twin.asset.linkExisting')}
                        </h4>
                        <button
                          onClick={() => setShowLinkTwinModal(true)}
                          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <LinkIcon className="h-4 w-4" />
                          {t('twin.detail.linkAsset')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Documents Tab */}
              {activeTab === 'documents' && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-sm font-semibold text-gray-900">
                      {t('common.documents')} ({(documents?.length || 0) + (externalDocument?.document ? 1 : 0)})
                    </h3>
                    <button
                      onClick={() => setShowUploadForm(!showUploadForm)}
                      className="inline-flex items-center gap-x-1 text-sm font-semibold text-brand-600 hover:text-brand-800"
                    >
                      <CloudArrowUpIcon className="h-4 w-4" />
                      {showUploadForm ? t('common.cancel') : t('pages.assets.uploadDocument')}
                    </button>
                  </div>

                  {/* Upload Form */}
                  {showUploadForm && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <h4 className="text-sm font-medium text-gray-900 mb-4">{t('pages.assets.uploadNewDocument')}</h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">{t('common.title')} *</label>
                          <input
                            type="text"
                            value={uploadTitle}
                            onChange={(e) => setUploadTitle(e.target.value)}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                            placeholder={t('pages.assets.documentTitlePlaceholder')}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">{t('pages.assets.documentType')}</label>
                            <select
                              value={uploadType}
                              onChange={(e) => setUploadType(e.target.value)}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                            >
                              <option value="manual">{t('pages.assets.documentTypes.manual')}</option>
                              <option value="wiring_diagram">{t('pages.assets.documentTypes.wiring_diagram')}</option>
                              <option value="ce_declaration">{t('pages.assets.documentTypes.ce_declaration')}</option>
                              <option value="parts_list">{t('pages.assets.documentTypes.parts_list')}</option>
                              <option value="maintenance_schedule">{t('pages.assets.documentTypes.maintenance_schedule')}</option>
                              <option value="other">{t('pages.assets.documentTypes.other')}</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">{t('common.language')}</label>
                            <select
                              value={uploadLanguage}
                              onChange={(e) => setUploadLanguage(e.target.value)}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                            >
                              <option value="en">English</option>
                              <option value="de">Deutsch</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">{t('common.file')} *</label>
                          <input
                            ref={fileInputRef}
                            type="file"
                            onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                            className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                          />
                        </div>
                        <div className="flex justify-end gap-3">
                          <button
                            type="button"
                            onClick={() => setShowUploadForm(false)}
                            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                          >
                            {t('common.cancel')}
                          </button>
                          <button
                            type="button"
                            onClick={() => uploadMutation.mutate()}
                            disabled={!uploadFile || !uploadTitle || uploadMutation.isPending}
                            className="rounded-md bg-brand-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {uploadMutation.isPending ? t('common.uploading') : t('common.upload')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* External Documents Section (from Twin Engine) */}
                  {externalDocument?.document && (
                    <div className="mb-6">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                        {t('pages.assets.externalDocuments')}
                      </h4>
                      <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0">
                            <div className="h-12 w-12 rounded-lg bg-purple-100 flex items-center justify-center">
                              <DocumentTextIcon className="h-6 w-6 text-purple-600" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="px-2 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-700">
                                {t('pages.assets.twinEngine')}
                              </span>
                              <span className="text-xs text-gray-500 uppercase">{externalDocument.document.language}</span>
                            </div>
                            <p className="text-sm font-medium text-gray-900">{externalDocument.document.title}</p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                              <span>{externalDocument.document.pages} {t('pages.assets.pages')}</span>
                              <span className="text-gray-300">|</span>
                              <span>{(externalDocument.document.file_size_bytes / 1024 / 1024).toFixed(2)} MB</span>
                            </div>
                            {externalDocument.document.description && (
                              <p className="mt-2 text-sm text-gray-600">{externalDocument.document.description}</p>
                            )}
                          </div>
                          <div className="flex-shrink-0">
                            <a
                              href={twinApi.getSampleDocumentUrl(asset.twinSampleId!)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 transition-colors"
                            >
                              <DocumentTextIcon className="h-4 w-4" />
                              {t('pages.assets.viewPdf')}
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Local Documents Section */}
                  {(documents && documents.length > 0) || externalDocument?.document ? (
                    <div>
                      {externalDocument?.document && (
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                          {t('pages.assets.localDocuments')}
                        </h4>
                      )}
                      {documents && documents.length > 0 ? (
                        <div className="space-y-3">
                          {documents.map((doc) => {
                            const typeConfig = DOCUMENT_TYPE_CONFIG[doc.documentType] || DOCUMENT_TYPE_CONFIG.other
                            return (
                              <div key={doc.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                <div className="flex items-center gap-3">
                                  <DocumentTextIcon className="h-8 w-8 text-gray-400" />
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">{doc.title}</p>
                                    <div className="flex items-center gap-2 mt-1">
                                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${typeConfig.color}`}>
                                        {t(`pages.assets.documentTypes.${typeConfig.key}` as TranslationKey)}
                                      </span>
                                      <span className="text-xs text-gray-500 uppercase">{doc.language}</span>
                                      {doc.version && (
                                        <span className="text-xs text-gray-500">v{doc.version}</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleDownload(doc.id)}
                                  disabled={downloadingDoc === doc.id}
                                  className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-800 text-sm font-medium disabled:opacity-50"
                                >
                                  {downloadingDoc === doc.id ? (
                                    <>
                                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                      </svg>
                                      {t('common.loading')}
                                    </>
                                  ) : (
                                    <>
                                      <ArrowDownTrayIcon className="h-4 w-4" />
                                      {t('common.download')}
                                    </>
                                  )}
                                </button>
                              </div>
                            )
                          })}
                        </div>
                      ) : externalDocument?.document ? (
                        <div className="text-center py-8 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-500">{t('pages.assets.noLocalDocuments')}</p>
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <DocumentArrowDownIcon className="mx-auto h-12 w-12 text-gray-300" />
                      <p className="mt-2 text-sm text-gray-500">
                        {t('pages.assets.noDocuments')}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {t('pages.assets.noDocumentsHint')}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* BOM Tab */}
              {activeTab === 'bom' && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-sm font-semibold text-gray-900">{t('pages.assets.bom')} ({bomItems?.length || 0} {t('pages.assets.bomItems')})</h3>
                  </div>
                  {bomItems && bomItems.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead>
                          <tr>
                            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('pages.assets.tableHeaders.position')}</th>
                            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('pages.assets.tableHeaders.partNumber')}</th>
                            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('pages.assets.tableHeaders.description')}</th>
                            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('pages.assets.tableHeaders.quantity')}</th>
                            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('pages.assets.tableHeaders.price')}</th>
                            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('common.actions')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {bomItems.map((item) => (
                            <tr key={item.id} className="hover:bg-gray-50">
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">{item.position}</td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                                <code className="bg-gray-100 px-2 py-0.5 rounded">{item.partNumber}</code>
                              </td>
                              <td className="px-3 py-4 text-sm text-gray-500">{item.description || '-'}</td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{item.quantity}</td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {item.listPrice ? (
                                  <span className="font-medium">{item.currency || 'EUR'} {Number(item.listPrice).toFixed(2)}</span>
                                ) : '-'}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm">
                                <Link
                                  to={`/service-cases/new?assetId=${asset.id}&partNumber=${encodeURIComponent(item.partNumber)}&category=part_request`}
                                  className="inline-flex items-center gap-1 text-purple-600 hover:text-purple-800 font-medium"
                                >
                                  <ShoppingCartIcon className="h-4 w-4" />
                                  {t('pages.assets.requestPart')}
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <WrenchScrewdriverIcon className="mx-auto h-12 w-12 text-gray-300" />
                      <p className="mt-2 text-sm text-gray-500">
                        {t('pages.assets.noBomItems')}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Service Cases Tab */}
              {activeTab === 'cases' && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-sm font-semibold text-gray-900">{t('nav.serviceCases')} ({cases.length})</h3>
                    <Link
                      to={`/service-cases/new?assetId=${asset.id}`}
                      className="inline-flex items-center gap-x-1 text-sm font-semibold text-brand-600 hover:text-brand-800"
                    >
                      <PlusIcon className="h-4 w-4" />
                      {t('pages.assets.newCase')}
                    </Link>
                  </div>
                  {cases.length > 0 ? (
                    <div className="space-y-3">
                      {cases.map((serviceCase) => (
                        <Link
                          key={serviceCase.id}
                          to={`/service-cases/${serviceCase.id}`}
                          className="block p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-900">{serviceCase.caseNumber}</span>
                            <div className="flex items-center gap-2">
                              <ServiceCaseStatusBadge status={serviceCase.status} />
                              <ServiceCasePriorityBadge priority={serviceCase.priority} />
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 truncate">{serviceCase.subject}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(serviceCase.createdAt).toLocaleDateString()}
                          </p>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <WrenchScrewdriverIcon className="mx-auto h-12 w-12 text-gray-300" />
                      <p className="mt-2 text-sm text-gray-500">
                        {t('pages.assets.noCases')}
                      </p>
                      <Link
                        to={`/service-cases/new?assetId=${asset.id}`}
                        className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-800"
                      >
                        <PlusIcon className="h-4 w-4" />
                        {t('pages.assets.createFirstCase')}
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {/* History Tab */}
              {activeTab === 'history' && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">{t('pages.assets.historyTimeline')}</h3>
                  {history && history.length > 0 ? (
                    <div className="flow-root">
                      <ul className="-mb-8">
                        {history.map((event, eventIdx) => (
                          <li key={event.id}>
                            <div className="relative pb-8">
                              {eventIdx !== history.length - 1 && (
                                <span
                                  className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200"
                                  aria-hidden="true"
                                />
                              )}
                              <div className="relative flex space-x-3">
                                <div>
                                  <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${
                                    event.status === 'closed' ? 'bg-gray-400' :
                                    event.status === 'resolved' ? 'bg-green-500' :
                                    event.status === 'in_progress' ? 'bg-blue-500' :
                                    event.status === 'waiting_customer' || event.status === 'waiting_parts' ? 'bg-amber-500' :
                                    'bg-brand-500'
                                  }`}>
                                    <WrenchScrewdriverIcon className="h-4 w-4 text-white" />
                                  </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between">
                                    <Link
                                      to={`/service-cases/${event.id}`}
                                      className="text-sm font-medium text-gray-900 hover:text-brand-600"
                                    >
                                      {event.caseNumber}
                                    </Link>
                                    <div className="flex items-center gap-2">
                                      <ServiceCaseStatusBadge status={event.status as any} />
                                      <ServiceCaseCategoryBadge category={event.category as any} />
                                    </div>
                                  </div>
                                  <p className="mt-1 text-sm text-gray-600">{event.subject}</p>
                                  <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                                    <span>{t('fields.created')}: {new Date(event.createdAt).toLocaleDateString()}</span>
                                    {event.resolvedAt && (
                                      <span className="text-green-600">
                                        {t('pages.assets.resolved')}: {new Date(event.resolvedAt).toLocaleDateString()}
                                      </span>
                                    )}
                                    {event.closedAt && (
                                      <span className="text-gray-600">
                                        {t('pages.assets.closed')}: {new Date(event.closedAt).toLocaleDateString()}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <ClockIcon className="mx-auto h-12 w-12 text-gray-300" />
                      <p className="mt-2 text-sm text-gray-500">
                        {t('pages.assets.noHistory')}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {t('pages.assets.noHistoryHint')}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Cartridge Tabs */}
              {isCartridgeTab(activeTab) && (
                <div>
                  {(() => {
                    const cartridgeTabData = getCartridgeTabData(activeTab)
                    if (!cartridgeTabData) {
                      return (
                        <div className="text-center py-12">
                          <p className="text-sm text-gray-500">Tab not found</p>
                        </div>
                      )
                    }
                    return (
                      <CartridgeTabContent
                        entityId={id!}
                        entityType="asset"
                        tab={cartridgeTabData}
                      />
                    )
                  })()}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Link Twin Project Modal */}
      {showLinkTwinModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">{t('twin.asset.linkExisting')}</h3>
            <p className="mt-2 text-sm text-gray-500">
              {t('twin.upload.selectAsset')}
            </p>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('twin.projects.title')}
              </label>
              <select
                value={selectedTwinProject}
                onChange={(e) => setSelectedTwinProject(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
              >
                <option value="">-- {t('common.select')} --</option>
                {availableTwinProjects?.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                    {project.status !== 'ready' && ` (${t(`twin.status.${project.status}`)})`}
                  </option>
                ))}
              </select>
              {availableTwinProjects?.length === 0 && (
                <p className="mt-2 text-sm text-gray-500">
                  {t('twin.projects.empty')}
                </p>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowLinkTwinModal(false)
                  setSelectedTwinProject('')
                }}
                className="px-3 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => {
                  if (selectedTwinProject) {
                    linkTwinMutation.mutate(selectedTwinProject)
                  }
                }}
                disabled={!selectedTwinProject || linkTwinMutation.isPending}
                className="px-3 py-2 text-sm font-semibold text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50"
              >
                {linkTwinMutation.isPending ? t('common.loading') : t('twin.detail.linkAsset')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
